/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  useTheme,
  alpha,
  InputAdornment,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { OptionType } from 'types';
import { PositionLeg } from 'types/optionsCalculator';
import DeleteIcon from '@mui/icons-material/Delete';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useCallback, useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import { convertToDaysTillExpiry, getExpirationLabel } from 'util/iVol';

interface EditableOptionLegProps {
  leg: PositionLeg;
  expirationOptions: number[];
  onEditLeg: (legId: string, updatedLeg: Partial<PositionLeg>) => void;
  onDeleteLeg: (legId: string) => void;
}

const EditableOptionLeg = ({
  leg,
  onEditLeg,
  expirationOptions,
  onDeleteLeg,
}: EditableOptionLegProps) => {
  const theme = useTheme();
  const [localQuantity, setLocalQuantity] = useState(Math.abs(leg.quantity));
  const [localStrike, setLocalStrike] = useState<number | null>(leg.strike);
  const [localPrice, setLocalPrice] = useState<number | null>(leg.price);
  const [expirySelectorOpen, setExpirySelectorOpen] = useState(false);
  const [localExpiration, setLocalExpiration] = useState<number>(
    leg.expiration,
  );
  const [expirationError, setExpirationError] = useState<string | null>(null);

  const serverPosColor = useRecoilValue(positiveTrendColorState);
  const serverNegColor = useRecoilValue(negativeTrendColorState);

  const safeExpirationOptions = useMemo(() => {
    const options = [...expirationOptions];

    // If current leg expiration is not in options, add it
    if (leg.expiration && !options.includes(leg.expiration)) {
      options.push(leg.expiration);
      options.sort((a, b) => a - b); // Keep chronological order
    }

    return options;
  }, [expirationOptions, leg.expiration]);

  // Update local state when props change
  useEffect(() => {
    setLocalQuantity(Math.abs(leg.quantity));
    setLocalStrike(leg.strike);
    setLocalPrice(leg.price);
    setLocalExpiration(leg.expiration);
  }, [leg.quantity, leg.strike, leg.price, leg.expiration]);

  // Debounced handlers
  const debouncedEditLeg = useCallback(
    debounce((legId: string, updatedLeg: Partial<PositionLeg>) => {
      onEditLeg(legId, updatedLeg);
    }, 300),
    [onEditLeg],
  );

  const handleQuantityChange = (newQuantity: number) => {
    setLocalQuantity(Math.abs(newQuantity));
    debouncedEditLeg(leg.id, { quantity: newQuantity });
  };

  const handleTypeChange = (newType: OptionType) => {
    onEditLeg(leg.id, { type: newType });
  };

  const handleStrikeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalStrike(null);
    } else {
      const newStrike = parseFloat(value);
      if (!isNaN(newStrike)) {
        setLocalStrike(newStrike);
        debouncedEditLeg(leg.id, { strike: newStrike });
      }
    }
  };

  const handleOpen = () => {
    setExpirySelectorOpen(true);
  };

  const handleClose = () => {
    setExpirySelectorOpen(false);
  };

  const handleExpirationChange = (event: SelectChangeEvent<number>) => {
    const newExpiration = Number(event.target.value);

    // Validate the date
    if (!newExpiration) {
      setExpirationError('Date is required');
      return;
    }

    const dateObj = dayjs(newExpiration);
    const today = dayjs();

    if (!dateObj.isValid()) {
      setExpirationError('Invalid date format');
      return;
    }

    // Update local state with timestamp
    setLocalExpiration(newExpiration);

    if (dateObj.isBefore(today, 'day')) {
      setExpirationError('Date cannot be in the past');
      return;
    }

    // Check if it's a valid market day (simplified check - could be enhanced)
    const isWeekend = dateObj.day() === 0 || dateObj.day() === 6;
    setExpirationError(isWeekend ? 'Warning: Weekend date selected' : null);

    // Only update if date is valid
    if (dateObj.isValid() && !dateObj.isBefore(today, 'day')) {
      debouncedEditLeg(leg.id, { expiration: newExpiration });
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalPrice(null);
    } else {
      const newPrice = parseFloat(value);
      if (!isNaN(newPrice)) {
        setLocalPrice(newPrice);
        debouncedEditLeg(leg.id, { price: newPrice });
      }
    }
  };

  const shouldRenderExpirationSelect = safeExpirationOptions.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3 },
        gap: '16px',
      }}
    >
      <Box
        sx={{
          mt: { xs: 3, sm: 0 },
          mr: { xs: 0, sm: 3 },
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          mb: { xs: 1, sm: 0 },
        }}
      >
        <Button
          size="small"
          variant="contained"
          onClick={() =>
            handleQuantityChange(
              leg.quantity > 0
                ? -Math.abs(leg.quantity)
                : Math.abs(leg.quantity),
            )
          }
          sx={{
            minWidth: '60px',
            position: 'relative',
            color: theme.palette.getContrastText(
              leg.quantity > 0 ? serverPosColor : serverNegColor,
            ),
            backgroundColor: leg.quantity > 0 ? serverPosColor : serverNegColor,
            '&:after': {
              content: '"⇄"',
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              color: theme.palette.getContrastText(
                leg.quantity > 0 ? serverNegColor : serverPosColor,
              ),
              backgroundColor:
                leg.quantity > 0 ? serverNegColor : serverPosColor,
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            },
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 0.2s',
              backgroundColor:
                leg.quantity > 0
                  ? alpha(serverPosColor, 0.9)
                  : alpha(serverNegColor, 0.9),
            },
          }}
        >
          {leg.quantity > 0 ? 'BTO' : 'STO'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' },
        }}
      >
        <FormControl size="small" sx={{ width: { xs: '45%', sm: 80 } }}>
          <InputLabel id="quantity-input-label">Quantity</InputLabel>
          <OutlinedInput
            id="quantity-input"
            value={localQuantity}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value)) {
                setLocalQuantity(value);
                handleQuantityChange(leg.quantity > 0 ? value : -value);
              }
            }}
            endAdornment={<InputAdornment position="end">x</InputAdornment>}
            label="Quantity"
            type="number"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '12px',
                '-moz-appearance': 'textfield',
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
              svg: {
                color: alpha(theme.palette.primary.main, 0.5),
              },
            }}
          />
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            handleTypeChange(
              leg.type === OptionType.CALL ? OptionType.PUT : OptionType.CALL,
            )
          }
          color={leg.type === OptionType.CALL ? 'success' : 'error'}
          sx={{
            minWidth: '40px',
            width: { xs: '45%', sm: 'auto' },
          }}
        >
          {leg.type === OptionType.CALL ? 'C' : 'P'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 4, sm: 3 },
          width: '100%',
        }}
      >
        <FormControl size="small" sx={{ width: { xs: '100%', sm: 120 } }}>
          <InputLabel id="strike-input-label">Strike</InputLabel>
          <OutlinedInput
            id="strike-input"
            value={localStrike}
            onChange={handleStrikeChange}
            type="number"
            inputProps={{ step: 0.1 }}
            label="Strike"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '12px',
                '-moz-appearance': 'textfield',
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
              svg: {
                color: alpha(theme.palette.primary.main, 0.5),
              },
            }}
          />
        </FormControl>

        {/* FIXED: Conditional rendering and safe options */}
        {shouldRenderExpirationSelect ? (
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 120 } }}>
            <InputLabel id="expiry-date-select-label">Expiration</InputLabel>
            <Select
              labelId="expiry-date-select-label"
              id="expiry-date-select"
              open={expirySelectorOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              onChange={handleExpirationChange}
              value={localExpiration}
              size="small"
              input={
                <OutlinedInput
                  label="Expiration"
                  sx={{
                    svg: {
                      color: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              renderValue={(selected: number) =>
                `${dayjs(selected).format('YYYY-MM-DD')}`
              }
              MenuProps={{
                sx: {
                  zIndex: 10001,
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  },
                },
                PaperProps: {
                  style: {
                    width: '150px',
                    maxHeight: '250px',
                  },
                },
              }}
            >
              {safeExpirationOptions.map((utcTime: number) => {
                return (
                  <MenuItem
                    key={utcTime}
                    value={utcTime}
                    sx={{
                      padding: '10px',
                    }}
                  >
                    <Checkbox
                      checked={leg.expiration === utcTime}
                      style={{
                        padding: '0px 10px 0px 0px',
                      }}
                    />
                    <ListItemText
                      primary={`${getExpirationLabel(utcTime.toString())}`}
                      secondary={`${convertToDaysTillExpiry(
                        dayjs().valueOf(),
                        utcTime,
                      )} DTE`}
                      primaryTypographyProps={{
                        fontSize: '13px',
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : (
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 120 } }}>
            <InputLabel id="expiry-date-loading-label">Expiration</InputLabel>
            <OutlinedInput
              id="expiry-date-loading"
              value={dayjs(localExpiration).format('YYYY-MM-DD')}
              label="Expiration"
              disabled
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontSize: '12px',
                  color: 'text.secondary',
                },
              }}
            />
          </FormControl>
        )}

        <FormControl
          size="small"
          error={!!expirationError}
          sx={{ width: { xs: '100%', sm: 120 } }}
        >
          <InputLabel id="price-label">Price</InputLabel>
          <OutlinedInput
            id="price-input"
            value={localPrice}
            onChange={handlePriceChange}
            type="number"
            inputProps={{ step: 0.01 }}
            endAdornment={<InputAdornment position="end">$</InputAdornment>}
            label="Price"
            sx={{
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '& input': {
                '-moz-appearance': 'textfield',
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                {
                  '-webkit-appearance': 'none',
                  margin: 0,
                },
            }}
          />
        </FormControl>
      </Box>

      <Box
        sx={{
          ml: { xs: 0, sm: 'auto' },
          mt: { xs: 2, sm: 0 },
          display: 'flex',
          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={() => onDeleteLeg(leg.id)}
          sx={{ minWidth: 'auto', p: '4px 8px' }}
        >
          <DeleteIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export default EditableOptionLeg;
